import React from 'react';
import PipelineFinancials from './PipelineFinancials';
import Founding100Badge from './Founding100Badge';
import { getPhasesForDeal, getCloseProbability } from '../../config/phaseConfig';

const fmt = (val) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(val || 0);

const calculateNet = (deal) => {
  const contractPrice = parseFloat(deal.contractPrice) || 0;
  const commissionPercent = parseFloat(deal.commissionPercent) || 0;
  const commission = contractPrice * (commissionPercent / 100);
  const brokerSplitValue = parseFloat(deal.brokerSplitValue) || 0;
  const brokerSplit = deal.brokerSplitType === 'flat'
    ? brokerSplitValue
    : commission * (brokerSplitValue / 100);
  const transactionFee = parseFloat(deal.transactionFee) || 0;
  const tcFee = parseFloat(deal.tcFee) || 0;
  return commission - brokerSplit - transactionFee - tcFee;
};

// ── Donut Chart ───────────────────────────────────────────────────────────────
function PhaseDonut({ deal, size = 80 }) {
  const phases = getPhasesForDeal(deal.type);
  const tasks = deal.tasks || [];
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.38;
  const strokeWidth = size * 0.22;
  // const circumference = 2 * Math.PI * r;
  const gapDeg = 4;
  const totalGap = gapDeg * phases.length;
  const totalArc = 360 - totalGap;
  const arcPerPhase = totalArc / phases.length;

  // Calculate completion per phase
  const phaseData = phases.map(phase => {
    const phaseTasks = tasks.filter(t => t.theme === phase.theme);
    const total = phaseTasks.length;
    const done = phaseTasks.filter(t => t.status === 'complete').length;
    const pct = total > 0 ? done / total : 0;
    return { ...phase, pct, total, done };
  });

  // Overall close probability
  const closeProbability = getCloseProbability(deal, tasks);

  // Build SVG arcs
  const arcs = [];
  let currentAngle = -90; // start at top

  phaseData.forEach((phase, i) => {
    const arcDeg = arcPerPhase;
    const startAngle = currentAngle;
    const endAngle = currentAngle + arcDeg;

    const toRad = (deg) => (deg * Math.PI) / 180;
    const x1 = cx + r * Math.cos(toRad(startAngle));
    const y1 = cy + r * Math.sin(toRad(startAngle));
    const x2 = cx + r * Math.cos(toRad(endAngle));
    const y2 = cy + r * Math.sin(toRad(endAngle));
    const largeArc = arcDeg > 180 ? 1 : 0;

    // Background arc (empty)
    arcs.push(
      <path
        key={`bg-${i}`}
        d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`}
        fill="none"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth={strokeWidth}
        strokeLinecap="butt"
      />
    );

    // Filled arc (completion)
    if (phase.pct > 0) {
      const filledEndDeg = startAngle + arcDeg * phase.pct;
      const fx2 = cx + r * Math.cos(toRad(filledEndDeg));
      const fy2 = cy + r * Math.sin(toRad(filledEndDeg));
      const filledLargeArc = arcDeg * phase.pct > 180 ? 1 : 0;
      arcs.push(
        <path
          key={`fill-${i}`}
          d={`M ${x1} ${y1} A ${r} ${r} 0 ${filledLargeArc} 1 ${fx2} ${fy2}`}
          fill="none"
          stroke={phase.color}
          strokeWidth={strokeWidth}
          strokeLinecap="butt"
        />
      );
    }

    currentAngle = endAngle + gapDeg;
  });

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        {arcs}
      </svg>
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center', lineHeight: 1,
      }}>
        <div style={{ fontSize: size * 0.18, fontWeight: 800, color: '#e8f0fe', fontVariantNumeric: 'tabular-nums' }}>
          {closeProbability}%
        </div>
      </div>
    </div>
  );
}

// ── Phase Legend ──────────────────────────────────────────────────────────────
function PhaseLegend({ deal }) {
  const phases = getPhasesForDeal(deal.type);
  const tasks = deal.tasks || [];

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: 8 }}>
      {phases.map(phase => {
        const phaseTasks = tasks.filter(t => t.theme === phase.theme);
        const total = phaseTasks.length;
        const done = phaseTasks.filter(t => t.status === 'complete').length;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        return (
          <div key={phase.theme} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: phase.color, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: 'rgba(232,240,254,0.45)' }}>
              {phase.theme} <span style={{ color: phase.color, fontWeight: 700 }}>{pct}%</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Deal Card ─────────────────────────────────────────────────────────────────
function DealCard({ deal, getDealTitle, onSelectProject }) {
  const isMobile = window.innerWidth < 768;
  const net = calculateNet(deal);
  const tasks = deal.tasks || [];
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => t.status === 'complete').length;
  const overallPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const closeProbability = getCloseProbability(deal, tasks);

  const probabilityColor =
    closeProbability >= 80 ? '#75BB2E' :
    closeProbability >= 50 ? '#facc15' :
    closeProbability >= 25 ? '#fb923c' : '#94a3b8';

  if (isMobile) {
    return (
      <div
        onClick={() => onSelectProject && onSelectProject(deal)}
        style={{ ...s.card, flexDirection: 'row', alignItems: 'center', gap: 16, padding: '16px' }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#263c66'}
        onMouseLeave={e => e.currentTarget.style.borderColor = '#1e2f52'}
      >
        {/* Left — big donut */}
        <PhaseDonut deal={deal} size={90} />

        {/* Right — info stack */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Badge + probability */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              ...s.typeBadge,
              background: deal.type === 'Buyer' ? 'rgba(96,165,250,0.15)' : 'rgba(251,191,36,0.15)',
              color: deal.type === 'Buyer' ? '#60a5fa' : '#fbbf24',
              border: `1px solid ${deal.type === 'Buyer' ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)'}`,
            }}>
              {deal.type}
            </span>
            <span style={{ ...s.closeProbLabel, color: probabilityColor }}>
              {closeProbability}% to close
            </span>
          </div>

          {/* Deal title */}
          <div style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: '-0.2px' }}>
            {getDealTitle(deal)}
          </div>

          {/* Est Net */}
          {net > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(232,240,254,0.35)' }}>Est. Net</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#75BB2E', fontVariantNumeric: 'tabular-nums' }}>{fmt(net)}</span>
            </div>
          ) : (
            <div style={s.netEmpty}>No financials yet</div>
          )}

          {/* View Deal */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(232,240,254,0.5)' }}>
            View Deal →
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => onSelectProject && onSelectProject(deal)}
      style={s.card}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#263c66'}
      onMouseLeave={e => e.currentTarget.style.borderColor = '#1e2f52'}
    >
      {/* Left — Donut */}
      <div style={s.cardLeft}>
        <PhaseDonut deal={deal} size={88} />
        <div style={s.closeProbWrap}>
          <div style={{ ...s.closeProbLabel, color: probabilityColor }}>
            {closeProbability}% to close
          </div>
        </div>
      </div>

      {/* Middle — Deal Info */}
      <div style={s.cardMid}>
        <div style={s.cardTopRow}>
          <span style={{
            ...s.typeBadge,
            background: deal.type === 'Buyer' ? 'rgba(96,165,250,0.15)' : 'rgba(251,191,36,0.15)',
            color: deal.type === 'Buyer' ? '#60a5fa' : '#fbbf24',
            border: `1px solid ${deal.type === 'Buyer' ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)'}`,
          }}>
            {deal.type}
          </span>
          <span style={s.cardTitle}>{getDealTitle(deal)}</span>
        </div>

        {deal.propertyAddress && (
          <div style={s.cardAddress}>{deal.propertyAddress}</div>
        )}

        <PhaseLegend deal={deal} />

        {/* Progress bar */}
        <div style={s.progressWrap}>
          <div style={s.progressBg}>
            <div style={{ ...s.progressFill, width: `${overallPct}%`, background: probabilityColor }} />
          </div>
          <span style={s.progressPct}>{overallPct}% tasks done</span>
        </div>
      </div>

      {/* Right — Financials */}
      <div style={s.cardRight}>
        {net > 0 ? (
          <>
            <div style={s.netLabel}>Est. Net</div>
            <div style={s.netValue}>{fmt(net)}</div>
            <div style={s.netSub}>
              {fmt(deal.contractPrice ? parseFloat(deal.contractPrice) : 0)} contract
            </div>
          </>
        ) : (
          <div style={s.netEmpty}>No financials yet</div>
        )}
        <div style={s.viewBtn}>View Deal →</div>
      </div>
    </div>
  );
}

// ── Main Pipeline ─────────────────────────────────────────────────────────────
const SalesPipeline = ({ projects, getDealTitle, onSelectProject, closedDeals, orgId, orgName }) => {
  const isMobile = window.innerWidth < 768;

  // Attach close probability to each deal and sort by it descending
  const sortedDeals = [...(projects || [])]
    .map(deal => ({
      ...deal,
      closeProbability: getCloseProbability(deal, deal.tasks || []),
    }))
    .sort((a, b) => b.closeProbability - a.closeProbability);

  if (isMobile) {
    return (
      <div style={{ padding: 16 }}>
        <h1 style={s.pageTitle}>Dashboard</h1>
        <PipelineFinancials activeDeals={sortedDeals} closedDeals={closedDeals || []} />
        {sortedDeals.length === 0 ? (
          <div style={s.empty}>No active deals. Create a new deal to get started.</div>
        ) : (
          sortedDeals.map(deal => (
            <DealCard
              key={deal.id}
              deal={deal}
              getDealTitle={getDealTitle}
              onSelectProject={onSelectProject}
            />
          ))
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h1 style={{ ...s.pageTitle, marginBottom: 0 }}>{orgName || 'Dashboard'}</h1>
        <Founding100Badge orgId={orgId} />
      </div>
      <PipelineFinancials activeDeals={sortedDeals} closedDeals={closedDeals || []} />
      {sortedDeals.length === 0 ? (
        <div style={s.empty}>No active deals. Create a new deal to get started.</div>
      ) : (
        sortedDeals.map(deal => (
          <DealCard
            key={deal.id}
            deal={deal}
            getDealTitle={getDealTitle}
            onSelectProject={onSelectProject}
          />
        ))
      )}
    </div>
  );
};

export default SalesPipeline;

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  pageTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: '#071D39',
    marginBottom: 20,
    letterSpacing: '-0.5px',
  },
  empty: {
    background: '#fff',
    borderRadius: 12,
    padding: 48,
    textAlign: 'center',
    color: '#516469',
    fontSize: 14,
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    background: 'linear-gradient(135deg, #0e2a4a 0%, #1a3a5c 100%)',
    border: '1px solid #1e2f52',
    borderRadius: 16,
    padding: '20px 24px',
    marginBottom: 12,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  cardLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    width: 88,
  },
  closeProbWrap: {
    textAlign: 'center',
  },
  closeProbLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardMid: {
    flex: 1,
    minWidth: 0,
  },
  cardTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  },
  typeBadge: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    padding: '3px 8px',
    borderRadius: 6,
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#e8f0fe',
    letterSpacing: '-0.2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardAddress: {
    fontSize: 12,
    color: 'rgba(232,240,254,0.4)',
    marginBottom: 4,
  },
  progressWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 10,
  },
  progressBg: {
    flex: 1,
    height: 4,
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  progressPct: {
    fontSize: 10,
    color: 'rgba(232,240,254,0.35)',
    flexShrink: 0,
  },
  cardRight: {
    flexShrink: 0,
    textAlign: 'right',
    minWidth: 130,
  },
  netLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: 'rgba(232,240,254,0.35)',
    marginBottom: 4,
  },
  netValue: {
    fontSize: 24,
    fontWeight: 800,
    color: '#75BB2E',
    letterSpacing: '-0.5px',
    fontVariantNumeric: 'tabular-nums',
  },
  netSub: {
    fontSize: 11,
    color: 'rgba(232,240,254,0.3)',
    marginTop: 2,
  },
  netEmpty: {
    fontSize: 12,
    color: 'rgba(232,240,254,0.25)',
    fontStyle: 'italic',
  },
  viewBtn: {
    marginTop: 12,
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(232,240,254,0.4)',
  },
};